import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import "./Cart.css";

// ─── Icons ───────────────────────────────────────────────
const IconCart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <path d="M16 10a4 4 0 01-8 0"/>
  </svg>
);

const IconTrash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
    <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
  </svg>
);

const IconArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const IconArrowLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const IconTruck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
    <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
  </svg>
);

// ─── Component ────────────────────────────────────────────
export default function Cart() {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, getCartCount } = useCart();
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('authToken'));

  useEffect(() => {
    const checkLoginStatus = () => {
      setIsLoggedIn(!!localStorage.getItem('authToken'));
    };
    
    // Check login status on mount and set up listener for storage changes
    checkLoginStatus();
    window.addEventListener('storage', checkLoginStatus);
    
    return () => {
      window.removeEventListener('storage', checkLoginStatus);
    };
  }, []);

  const getPrice = (price) => {
    if (typeof price === 'string') {
      // Remove currency symbols and parse as float
      const cleaned = price.replace(/[KSh$\s,]/g, '');
      const numeric = parseFloat(cleaned);
      return isNaN(numeric) ? 0 : numeric;
    }
    return typeof price === 'number' ? price : 0;
  };

  const formatPrice = (price) => {
    const numeric = getPrice(price);
    return numeric.toFixed(0);
  };

  const subtotal = cartItems.reduce((s, it) => s + getPrice(it.price) * it.quantity, 0);
  const shipping = subtotal > 150 ? 0 : 9.99;
  const total = subtotal + shipping;
  const totalItems = getCartCount();

  const isEmpty = cartItems.length === 0;

  return (
    <div className="cart-page">

      {/* ── Main ── */}
      <main className="cart-main">
        <div className="cart-title-row">
          <h1 className="cart-title">Your Cart</h1>
          {!isEmpty && (
            <span className="cart-count-badge">
              {totalItems} {totalItems === 1 ? "item" : "items"}
            </span>
          )}
        </div>

        {/* ── Empty State ── */}
        {isEmpty ? (
          <div className="cart-empty">
            <div className="cart-empty-icon">
              <IconCart />
            </div>
            <h2>Your cart is empty</h2>
            <p>
              Looks like you haven't added anything yet. Explore our collection and
              find something you'll love.
            </p>
            <button className="btn-continue-lg" onClick={() => navigate("/shop")}>
              <IconArrowLeft />
              Continue Shopping
            </button>
          </div>
        ) : (
          /* ── Cart Grid ── */
          <div className="cart-grid">
            {/* ── Items ── */}
            <section className="cart-items-section">
              <div className="cart-items-header">
                <div>Product</div>
                <div>Quantity</div>
                <div style={{ textAlign: "right" }}>Unit Price</div>
                <div style={{ textAlign: "right" }}>Total</div>
              </div>

              <div className="cart-items-list">
                {cartItems.map((item) => (
                  <div className="cart-item" key={item.id}>
                    {/* Info */}
                    <div className="cart-item-info">
                      <div className="cart-item-img-placeholder">
                        <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                      </div>
                      <div className="cart-item-details">
                        <div className="cart-item-name">{item.name}</div>
                        <div className="cart-item-variant">{(item.description || '').length > 30 ? (item.description || '').substring(0, 30) + '...' : (item.description || '')}</div>
                        <button
                          className="cart-item-remove"
                          onClick={() => removeFromCart(item.id, item.name)}
                        >
                          <IconTrash /> Remove
                        </button>
                      </div>
                    </div>

                    {/* Qty */}
                    <div className="qty-control">
                      <button
                        className="qty-btn"
                        onClick={() => updateQuantity(item.id, -1)}
                        disabled={item.quantity <= 1}
                      >
                        −
                      </button>
                      <span className="qty-value">{item.quantity}</span>
                      <button
                        className="qty-btn"
                        onClick={() => updateQuantity(item.id, 1)}
                      >
                        +
                      </button>
                    </div>

                    {/* Unit Price */}
                    <div className="cart-item-price">
                      Ksh.{formatPrice(item.price)}
                    </div>

                    {/* Line Total */}
                    <div className="cart-item-total">
                      Ksh.{formatPrice(getPrice(item.price) * item.quantity)}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* ── Summary ── */}
            <aside className="cart-summary">
              <h2 className="cart-summary-title">Order Summary</h2>

              <div className="summary-row">
                <span className="label">Subtotal ({totalItems} items)</span>
                <span className="value">Ksh.{formatPrice(subtotal)}</span>
              </div>

              {/* <div className={`summary-row ${shipping === 0 ? "free" : ""}`}>
                <span className="label">Shipping</span>
                <span className="value">
                  {shipping === 0 ? "Free" : `$Ksh.{shipping.toFixed(2)}`}
                </span>
              </div> */}

              {shipping > 0 && (
                <p style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: -8, marginBottom: 8 }}>
                </p>
              )}

              <hr className="summary-divider" />

              <div className="summary-row total">
                <span className="label">Total</span>
                <span className="value">Ksh.{formatPrice(total)}</span>
              </div>

              {/* Promo */}
              <hr className="summary-divider" />

              {/* Checkout */}
              <button 
                className="btn-checkout" 
                onClick={() => isLoggedIn ? navigate("/checkout") : navigate("/login")}
              >
                {isLoggedIn ? "Proceed to Checkout" : "Login to Checkout"} <IconArrowRight />
              </button>

              {/* Continue Shopping */}
              <button className="btn-continue" onClick={() => navigate("/shop")}>
                <IconArrowLeft /> Continue Shopping
              </button>

              {/* Trust Badges */}
              <div className="trust-badges">
                <div className="trust-badge">
                  <IconShield />
                  <span>Secure checkout </span>
                </div>
                <div className="trust-badge">
                  <IconTruck />
                  <span>Free returns within 7 days</span>
                </div>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
